/* aa-session.js - shared cut-tool session gate for All Aluminium.
   Purpose: when the AluCut worker's Turnstile gate is ON, obtain a short-lived
   signed session (Cloudflare Turnstile -> /session) and attach it to every
   /quote call as the X-AA-Session header. When the gate is OFF (no site key),
   this is a transparent no-op: window.AACut.quote() behaves like a plain fetch.
   Safe to ship before any keys exist. ASCII only. */
(function () {
  'use strict';
  var CFG = (typeof window !== 'undefined' && window.AA_CUT_CFG) ? window.AA_CUT_CFG : {};
  // === CENTRAL TURNSTILE SITE KEY ===============================================
  // To turn the gate ON everywhere, paste your Cloudflare Turnstile SITE key here
  // (the public one) and re-upload this one file. Leave blank to keep the gate OFF
  // store-wide. A per-section AA_CUT_CFG.turnstileSiteKey (if set) still overrides.
  var AA_SITE_KEY = '0x4AAAAAADz5D6VwcmZZsGc-';
  // ==============================================================================
  // Site key enables the gate on the client. Empty/absent => gate off => no-op.
  var SITE_KEY = (CFG.turnstileSiteKey || AA_SITE_KEY || '');
  var QUOTE_EP = CFG.quoteEndpoint || 'https://alucut.allaluminium.workers.dev/quote';
  var SESSION_EP = CFG.sessionEndpoint || QUOTE_EP.replace(/\/quote(\?.*)?$/, '/session');
  var TS_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

  var state = { token: null, exp: 0, widgetId: null, container: null, tsLoading: null };

  function now() { return Date.now(); }
  function haveValidSession() { return !!state.token && now() < (state.exp - 5000); }

  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve();
    if (state.tsLoading) return state.tsLoading;
    state.tsLoading = new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = TS_SRC; s.async = true; s.defer = true;
      s.onload = function () { resolve(); };
      s.onerror = function () { reject(new Error('turnstile-load-failed')); };
      document.head.appendChild(s);
    });
    return state.tsLoading;
  }

  // Build a modal overlay (hidden by default) whose inner box hosts the Turnstile
  // widget. For MANAGED mode most visitors pass invisibly and this never shows; when
  // Cloudflare needs an interactive challenge, we reveal the overlay so the customer
  // can complete it (never hidden - a hidden challenge silently blocks real people).
  function ensureContainer() {
    if (state.container) return state.container;
    var overlay = document.createElement('div');
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.cssText = 'display:none;position:fixed;inset:0;z-index:2147483600;background:rgba(15,23,42,0.55);align-items:center;justify-content:center;';
    var card = document.createElement('div');
    card.style.cssText = 'background:#fff;border-radius:12px;padding:22px 22px 18px;max-width:340px;width:90%;box-shadow:0 12px 44px rgba(0,0,0,.32);text-align:center;font:14px/1.5 -apple-system,Segoe UI,Roboto,sans-serif;color:#0f172a;';
    var title = document.createElement('div');
    title.textContent = 'Quick security check';
    title.style.cssText = 'font-weight:700;font-size:15px;margin-bottom:4px;';
    var sub = document.createElement('div');
    sub.textContent = 'Just confirming you are human so we can price your cut - it only takes a moment.';
    sub.style.cssText = 'color:#64748b;font-size:12.5px;margin-bottom:14px;';
    var widget = document.createElement('div');
    widget.style.cssText = 'display:flex;justify-content:center;min-height:65px;';
    var x = document.createElement('button');
    x.type = 'button'; x.textContent = '\u00d7'; x.setAttribute('aria-label', 'Close');
    x.style.cssText = 'position:absolute;top:8px;right:10px;background:none;border:none;font-size:20px;line-height:1;color:#94a3b8;cursor:pointer;padding:2px 6px;';
    x.onclick = function () { hideChallenge(); if (state.dismiss) { var d = state.dismiss; state.dismiss = null; d('dismissed'); } };
    card.style.position = 'relative';
    card.appendChild(x); card.appendChild(title); card.appendChild(sub); card.appendChild(widget);
    overlay.appendChild(card);
    document.body.appendChild(overlay);
    state.overlay = overlay;
    state.container = widget;
    return widget;
  }
  function showChallenge() { if (state.overlay) { state.overlay.style.display = 'flex'; state.overlay.setAttribute('aria-hidden', 'false'); } }
  function hideChallenge() { if (state.overlay) { state.overlay.style.display = 'none'; state.overlay.setAttribute('aria-hidden', 'true'); } }

  // Get ONE Turnstile token (single-use). Managed widget: usually invisible; only
  // reveals the overlay when an interactive challenge is actually required.
  function getTurnstileToken() {
    return loadTurnstile().then(function () {
      return new Promise(function (resolve, reject) {
        var box = ensureContainer();
        var done = false, to = null;
        /* r-no-reset-loop 22 Aug 2026: remove the widget the moment this call
           settles, success or failure. A widget left alive after success re-runs
           a challenge every ~5 min via expired-callback -> reset, forever, while
           the tab stays open (measured in Turnstile analytics: ~1.5k challenges
           per day, flat overnight) - and any re-run can escalate to the
           interactive popup appearing UNPROMPTED minutes after the customer last
           touched anything (seen in a Clarity replay, 21 Aug). The signed
           session lives ~30 min server-side; a live widget serves no purpose
           once its token has been exchanged. The next call re-renders fresh. */
        function removeWidget() {
          if (state.widgetId !== null && window.turnstile && window.turnstile.remove) {
            try { window.turnstile.remove(state.widgetId); } catch (e) {}
          }
          state.widgetId = null;
          try { box.innerHTML = ''; } catch (e) {}
        }
        function finishOk(tok) { if (!done) { done = true; if (to) clearTimeout(to); hideChallenge(); removeWidget(); resolve(tok); } }
        function finishErr(e) { if (!done) { done = true; if (to) clearTimeout(to); hideChallenge(); removeWidget(); reject(e instanceof Error ? e : new Error(String(e))); } }
        state.dismiss = finishErr;
        var opts = {
          sitekey: SITE_KEY,
          callback: function (tok) { hideChallenge(); finishOk(tok); },
          'expired-callback': function () { try { window.turnstile.reset(state.widgetId); } catch (e) {} },
          // Cloudflare is about to show an interactive challenge -> reveal the box
          // and stop the safety timeout so the customer can solve it at their pace.
          'before-interactive-callback': function () { if (to) { clearTimeout(to); to = null; } showChallenge(); },
          'error-callback': function () { finishErr('turnstile-error'); },
          'timeout-callback': function () { finishErr('turnstile-timeout'); }
        };
        try {
          // Fresh render each call so callbacks bind to this promise.
          if (state.widgetId !== null && window.turnstile && window.turnstile.remove) {
            try { window.turnstile.remove(state.widgetId); } catch (e) {}
            state.widgetId = null; box.innerHTML = '';
          }
          state.widgetId = window.turnstile.render(box, opts);
        } catch (e) { finishErr(e); }
        // Safety timeout only while it's still invisible (cleared once a challenge shows).
        to = setTimeout(function () { finishErr('turnstile-slow'); }, 25000);
      });
    });
  }

  // Ensure we hold a valid signed session token. Resolves to token or null.
  function ensureSession(force) {
    if (!SITE_KEY) return Promise.resolve(null);          // gate off -> no token needed
    if (!force && haveValidSession()) return Promise.resolve(state.token);
    if (state.inflight) return state.inflight;            // one challenge at a time
    state.inflight = getTurnstileToken().then(function (tsToken) {
      return fetch(SESSION_EP, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: tsToken })
      }).then(function (r) { return r.json(); });
    }).then(function (j) {
      if (j && j.gate === 'off') { state.token = null; state.exp = 0; return null; }
      if (j && j.ok && j.session) {
        state.token = j.session;
        state.exp = now() + (j.expiresInMs || 1800000);
        return state.token;
      }
      state.token = null; state.exp = 0;
      return null;
    }).catch(function () { state.token = null; state.exp = 0; return null; })
      .then(function (v) { state.inflight = null; return v; });
    return state.inflight;
  }

  function doFetch(url, body, token) {
    var headers = { 'Content-Type': 'application/json' };
    if (token) headers['X-AA-Session'] = token;
    return fetch(url, { method: 'POST', headers: headers, body: JSON.stringify(body) });
  }

  // Public: POST a cut request, transparently handling the session gate.
  // Returns parsed JSON (same as a plain /quote fetch). Never throws - on a
  // network failure it resolves to { ok:false, errors:['Network error'] }.
  function quote(url, body) {
    url = url || QUOTE_EP;
    return ensureSession(false).then(function (token) {
      return doFetch(url, body, token).then(function (r) {
        if (r.status === 401) {
          // Gate turned on (or session expired) mid-flight: re-challenge once.
          return ensureSession(true).then(function (t2) {
            return doFetch(url, body, t2).then(function (r2) { return r2.json(); });
          });
        }
        return r.json();
      });
    }).catch(function () { return { ok: false, errors: ['Network error'] }; });
  }

  // Public: POST for a binary response (the PDF cut plan). Resolves to a Blob,
  // or null on failure. Handles the session gate the same way.
  function fetchBlob(url, body) {
    return ensureSession(false).then(function (token) {
      var headers = { 'Content-Type': 'application/json' };
      if (token) headers['X-AA-Session'] = token;
      return fetch(url, { method: 'POST', headers: headers, body: JSON.stringify(body) }).then(function (r) {
        if (r.status === 401) {
          return ensureSession(true).then(function (t2) {
            var h2 = { 'Content-Type': 'application/json' };
            if (t2) h2['X-AA-Session'] = t2;
            return fetch(url, { method: 'POST', headers: h2, body: JSON.stringify(body) }).then(function (r2) {
              return r2.ok ? r2.blob() : null;
            });
          });
        }
        return r.ok ? r.blob() : null;
      });
    }).catch(function () { return null; });
  }

  window.AACut = window.AACut || {};
  window.AACut.quote = quote;
  window.AACut.fetchBlob = fetchBlob;
  window.AACut.ensureSession = ensureSession;
  window.AACut.gateOn = function () { return !!SITE_KEY; };
})();
